/// <amd-module name="Artifacts/Nuget/RestClient/NuGet" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   packaging\client\nuget\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as NuGet from "Artifacts/Nuget/Contracts/NuGet";
import * as PackagingShared from "Artifacts/Root/types/PackagingShared";

import { INuGetRestClient } from "Artifacts/Nuget/RestClient/NuGet.Types";

export { INuGetRestClient } from "Artifacts/Nuget/RestClient/NuGet.Types";

class NuGetRestClient extends RestClientBase implements INuGetRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Download a package version directly.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     * @param sourceProtocolVersion - Unused
     */
    public async downloadPackage(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string,
        sourceProtocolVersion?: string
        ): Promise<ArrayBuffer> {

        const queryValues: any = {
            sourceProtocolVersion: sourceProtocolVersion
        };

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/versions/{packageVersion}/content",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            queryParams: queryValues
        });
    }

    /**
     * Update several packages from a single feed in a single request. The updates to the packages do not happen atomically.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updatePackageVersions(
        batchRequest: NuGet.NuGetPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete or restore several package versions from the recycle bin.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data. \<c\>Operation\</c\> must be \<c\>PermanentDelete\</c\> or \<c\>RestoreToFeed\</c\>
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackageVersions(
        batchRequest: NuGet.NuGetPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/RecycleBin/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete a package version from a feed's recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersionFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * View a package version's deletion/recycled status
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageVersionMetadataFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<NuGet.NuGetPackageVersionDeletionState> {

        return this.beginRequest<NuGet.NuGetPackageVersionDeletionState>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Restore a package version from a feed's recycle bin back into the active feed.
     * 
     * @param packageVersionDetails - Set the 'Deleted' member to 'false' to apply the restore operation
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async restorePackageVersionFromRecycleBin(
        packageVersionDetails: NuGet.NuGetRecycleBinPackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Get the upstreaming behavior of a package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param project - Project ID or project name
     */
    public async getUpstreamingBehavior(
        feedId: string,
        packageName: string,
        project?: string
        ): Promise<PackagingShared.UpstreamingBehavior> {

        return this.beginRequest<PackagingShared.UpstreamingBehavior>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            }
        });
    }

    /**
     * Set the upstreaming behavior of a package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param behavior - The behavior to apply to the package within the scope of the feed
     * @param project - Project ID or project name
     */
    public async setUpstreamingBehavior(
        feedId: string,
        packageName: string,
        behavior: PackagingShared.UpstreamingBehavior,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            },
            body: behavior
        });
    }

    /**
     * Send a package version from the feed to its paired recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package to delete.
     * @param packageVersion - Version of the package to delete.
     * @param project - Project ID or project name
     */
    public async deletePackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<NuGet.Package> {

        return this.beginRequest<NuGet.Package>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about a package version.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     * @param showDeleted - True to include deleted packages in the response.
     */
    public async getPackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string,
        showDeleted?: boolean
        ): Promise<NuGet.Package> {

        const queryValues: any = {
            showDeleted: showDeleted
        };

        return this.beginRequest<NuGet.Package>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            queryParams: queryValues
        });
    }

    /**
     * Set mutable state on a package version.
     * 
     * @param packageVersionDetails - New state to apply to the referenced package.
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package to update.
     * @param packageVersion - Version of the package to update.
     * @param project - Project ID or project name
     */
    public async updatePackageVersion(
        packageVersionDetails: NuGet.PackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/nuget/packages/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

}

export const NuGetClientName = "NuGet-AzureArtifacts.INuGetRestClient";

export function getNuGetClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): INuGetRestClient {
    return pageContext.getRestClient<INuGetRestClient>(NuGetClientName, options);
}

RestClients.add(NuGetClientName, { 
    factory: NuGetRestClient,
    options: {
        resourceAreaId: "b3be7473-68ea-4a81-bfc7-9530baaa19ad",
        serviceInstanceType: "00000030-0000-8888-8000-000000000000"
    }
});
